import * as React from "react";
import Confetti from 'react-dom-confetti';

import { Deck, Hand, Card, Suit } from "../playing-cards";
import { getSuitName, sortCards, getAllSuits, isCardAvailable, SUIT_IMAGES, getLeftBowerSuit, isLeftBower } from "../playing-cards/Suit";

import { CardComponent } from "./CardComponent";
import { getOpponents, images as opponentImages } from "../engine/Opponent";
import { ScoreboardComponent } from "./ScoreboardComponent";
import { Player } from "../engine/Player";
import { RoundData, getTeamPlayers } from "../engine/RoundData";
import { EndRoundComponent } from "./EndRoundComponent";

export interface IBoardState {
    cards: Card[],
    deck: Deck,
    hands: Hand[],
    players: Player[],

    dealer: number,
    currentPlayer: number,
    turn: number,
    playingCard?: Card,
    playedCards: number,

    playingAlone: boolean,
    showPlayingAloneDialog: boolean,

    trump: Suit,
    drawingCard?: Card,
    chooseTrump?: Suit[],
    discardNeeded?: boolean,

    rounds: RoundData[],
    currentRound: number,
    endOfRound: boolean,
}

// const OPPONENT_AI_TIMEOUT = 2000;
const OPPONENT_AI_TIMEOUT = 800;
const NEXT_ACTION_TIMEOUT = 1200;

const NOT_IN_GAME_TEAM = 'not-in-game';

const confettiConfig = {
  angle: 90,
  spread: 45,
  startVelocity: 45,
  elementCount: 50,
  decay: 0.9
};

export class BoardComponent extends React.Component<any, IBoardState> {
    deck: Deck;

    dealer: number;
    makers: number; // https://www.pagat.com/euchre/euchre.html#making_trump
    players: Player[];

    // 4 hands
    hands: Hand[] = [ new Hand(1), new Hand(2), new Hand(3), new Hand(4) ];
    rounds: RoundData[];
    currentRound: number;
    suitsPerRound: Suit[] = [];
    maxPlayedCards: number = 4;

    public componentWillMount() {
        const cachedRounds: any = window.localStorage.getItem("rounds");
        if (cachedRounds) {
            this.rounds = JSON.parse(cachedRounds);
            this.players = this.rounds[0].players.map(player => {
                // re-set player team
                player.team = undefined;
                player.image = opponentImages[player.name];
                return player;
            });
            this.dealer = (Number(window.localStorage.getItem("dealer")) + 1) % 4;
            this.currentRound = Number(window.localStorage.getItem("currentRound") || "0");

        } else {
            // create all players
            this.players = getOpponents(3).map(player => {
                player.score = 0;
                return player;
            });
            this.players.unshift({ name: "you", tricks: 0 } as Player);
            this.rounds = [];
            this.dealer = Math.floor(Math.random() * 4);
            this.currentRound = -1;
        }

        this.startRound();
    }

    startRound () {
        this.deck = new Deck({
            minNumber: 9,
            maxNumber: 14,
            shuffle: true,
            jokers: 0
        });

        // make sure all hands have no cards.
        this.hands.forEach(hand => hand.clear());
        this.players.forEach(player => player.tricks = 0);

        this.setState({
            dealer: this.dealer,
            currentPlayer: this.dealer,
            cards: this.deck.cards,
            hands: this.hands,
            players: this.players,
            rounds: this.rounds,
            turn: 0,
            playedCards: 0,
            currentRound: this.currentRound,
            endOfRound: false
        });

        //
        // animate card dealing process
        //
        setTimeout(() => {
            const dealStart = (this.dealer + 1) % 4;
            this.dealRound(dealStart, 2, () => {
                this.dealRound(dealStart, 3, () => {
                    this.sortCurrentPlayerHand();
                    this.nextTurn();
                });
            });
        }, 500);
    }

    dealCards = (hand: Hand, numCards: number, onComplete?: Function, timeout: number = 200) => {
        this.deck.deal(hand, numCards);

        this.setState({
            cards: this.deck.cards,
            hands: this.hands
        });

        if (onComplete) {
            setTimeout(onComplete, timeout);
        }
    }

    dealRound = (startFrom: number, numCards: number, onComplete?: Function) => {
        let i=0;
        const getHandIndex = () => (startFrom + i) % 4;

        this.dealCards(this.hands[getHandIndex()], numCards, () => {
            i++;
            this.dealCards(this.hands[getHandIndex()], numCards, () => {
                i++;
                this.dealCards(this.hands[getHandIndex()], numCards, () => {
                    i++;
                    this.dealCards(this.hands[getHandIndex()], numCards, onComplete);
                })
            })
        });
    }

    onPlayCard = (cardIndex: number) => {
        if (this.state.discardNeeded) {
            const drawingCard = this.state.drawingCard as Card;

            this.onOrderTrump(this.state.dealer, cardIndex);
            this.sortCurrentPlayerHand(drawingCard.suit);

            const nextState = {
                discardNeeded: false,
                hands: this.state.hands,
                players: this.players,
                cards: this.deck.cards,
                trump: drawingCard.suit,
                drawingCard: undefined
            };

            if (this.state.currentPlayer === 0) {
                this.setState(nextState);

            } else {
                this.setState(this.onRoundStart(nextState), () => this.nextTurn(false));
            }

        } else {
            const card = this.hands[this.state.currentPlayer].discard(cardIndex, true);
            card.played = this.state.playedCards;

            // flag player as already played.
            this.players[(card.player as number) - 1].played = true;

            const nextState: any = {
                cards: this.deck.cards,
                hands: this.hands,
                players: this.players,
                playedCards: this.state.playedCards + 1
            };

            if (!this.state.playingCard) {
                // clone card
                let playingCard = new Card(card.suit, card.number, card.player);

                // Left-bower suit is considered trump suit
                if (isLeftBower(card, this.state.trump)) {
                    playingCard.suit = this.state.trump;
                }

                nextState.playingCard = playingCard;
            }

            this.setState(nextState, () => {
                if (nextState.playedCards === this.maxPlayedCards) {
                    // time after all tricks are played in a single round
                    setTimeout(() => this.nextTurn(), NEXT_ACTION_TIMEOUT * 1.4);

                } else {
                    this.nextTurn();
                }
            });
        }
    }

    onPlayAnotherHand = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        // clean-up player data before storing it
        this.rounds.forEach(round => {
            round.players.forEach(player => {
                player.balloon = undefined;
                player.balloonText = undefined;
                player.image = undefined;
            });
        });

        window.localStorage.setItem("rounds", JSON.stringify(this.rounds));
        window.localStorage.setItem("dealer", JSON.stringify(this.dealer));
        window.localStorage.setItem("currentRound", JSON.stringify(this.state.currentRound));
        window.location.reload();
    }

    onResetScores = (e: React.MouseEvent<HTMLButtonElement>) => {

        e.preventDefault();
        window.localStorage.removeItem("rounds");
        window.localStorage.removeItem("dealer");
        window.localStorage.removeItem("currentRound");
        window.location.reload();
        if( !window.localStorage.getItem("rounds") && !window.localStorage.getItem("dealer") && !window.localStorage.getItem("currentRound") ) {

            this.setState({});
            this.players = getOpponents(3).map(player => {
                            player.score = 0;
                            return player;
                        });
                        this.players.unshift({ name: "you", tricks: 0 } as Player);
                        this.rounds = [];
                        this.dealer = Math.floor(Math.random() * 4);
                        this.currentRound = -1;
                        this.render();
        }else{
            alert("Sorry, internal error occurred, Please refresh the page.");
        }
    }

    nextTurn (changeCurrentPlayer: boolean = true) {
        let nextState: IBoardState & any = {};

        // reset player balloon
        nextState.players = this.state.players;
        nextState.players[this.state.currentPlayer].balloon = false;

        nextState.turn = this.state.turn + 1;
        nextState.currentPlayer = (changeCurrentPlayer)
            ? (this.state.currentPlayer + 1) % 4
            : this.state.currentPlayer;

        // skip player if he's not in the round (partner playing alone)
        if (
            changeCurrentPlayer &&
            this.state.trump &&
            this.players[nextState.currentPlayer].team === NOT_IN_GAME_TEAM
        ) {
            nextState.turn++;
            nextState.currentPlayer = (nextState.currentPlayer + 1) % 4;
        }

        if (this.state.turn === 0) {
            nextState.drawingCard = this.deck.draw();

        } else if ((this.state.turn % 4 === 0) && !this.state.drawingCard && !this.state.trump) {
            // move all cards back to the board. re-start the round.
            this.state.hands.forEach((hand) => {
                hand.cards.forEach((card) => {
                    card.player = undefined;
                });
            });

            this.setState({
                cards: this.deck.cards,
                drawingCard: undefined,
                chooseTrump: undefined
            });

            setTimeout(() => this.startRound(), NEXT_ACTION_TIMEOUT);
            return;

        } else if ((this.state.turn % 4) === 0 && !this.state.chooseTrump && !this.state.trump) {
            nextState.drawingCard = undefined;
            nextState.chooseTrump = getAllSuits().filter((suit) => suit !== (this.state.drawingCard as Card).suit);

        } else if (this.state.playedCards === this.maxPlayedCards) {
            // end of round!
            // check trick winner!
            const playedCards = this.state.cards.filter(card => card.played !== undefined);

            const winner = this.checkTrickWinner(playedCards);
            const winnerIndex = winner - 1;
            this.players[winnerIndex].tricks++;

            // allow players to play again
            this.players.forEach(player => player.played = false);

            nextState.cards = this.deck.cards;
            nextState.players = this.players;
            nextState.playedCards = 0;
            nextState.playingCard = undefined;
            nextState.currentPlayer = winnerIndex;
        }

        const isEndOfRound = this.hands.filter(hand => hand.cards.length === 0).length === this.maxPlayedCards;

        if (isEndOfRound) {
            this.onRoundEnd(nextState);

        } else if (nextState.currentPlayer !== 0) {
            //
            // Let AI act after a timeout.
            //
            setTimeout(() => this.actAI(), OPPONENT_AI_TIMEOUT);
        }

        this.setState(nextState);
    }

    onRoundStart (nextState: IBoardState & any) {
        // define each player's team
        this.makers = this.state.currentPlayer % 2;

        nextState.players.forEach((player: Player, i: number) => {
            if (player.team !== NOT_IN_GAME_TEAM) {
                player.team = ((i % 2) === this.makers) ? 'makers' : 'defenders';
            }
        });

        if (nextState.currentPlayer === undefined) {
            nextState.currentPlayer = (this.state.dealer + 1) % 4;
        }

        const newRound = new RoundData(nextState.players, nextState.trump || this.state.trump);
        nextState.currentRound = this.state.currentRound + 1;
        nextState.rounds = this.rounds;
        nextState.rounds.push(newRound);
        return nextState;
    }

    onRoundEnd (nextState: IBoardState & any) {
        const currentRound = this.state.rounds[this.state.currentRound];

        const teams = [
            getTeamPlayers(this.players, 0).filter(player => player.team !== NOT_IN_GAME_TEAM),
            getTeamPlayers(this.players, 1).filter(player => player.team !== NOT_IN_GAME_TEAM)
        ];

        const scores = [
            teams[0].reduce((acc, player) => acc += player.tricks, 0),
            teams[1].reduce((acc, player) => acc += player.tricks, 0)
        ];

        const winnerTeam = Number(scores[1] > scores[0]);
        const loserTeam = Number(scores[0] > scores[1]);

        currentRound.winner = winnerTeam;

        if (this.makers === winnerTeam) {
            const hasMaxScore = (scores[winnerTeam] === 5);
            currentRound.score = (hasMaxScore) ? 2 : 1;
            if (hasMaxScore && teams[winnerTeam].length === 1) {
                currentRound.score += 2;
            }

        } else {
            currentRound.score = (scores[loserTeam] < 3) ? 2 : 1;
        }

        nextState.trump = undefined;
        nextState.endOfRound = true;
        nextState.currentPlayer = undefined;
        nextState.rounds = this.state.rounds;

        return nextState;
    }

    getWinnerCard (cards: Card[]) {
        const playingCard = this.state.playingCard as Card;

        const trumpCards = cards.filter(card => {
            return card.suit === this.state.trump || isLeftBower(card, this.state.trump);
        });

        const sameSuitCards = cards.filter(card => card.suit === playingCard.suit);

        const orderedTrumpCards = this.sortCards(trumpCards, this.getSuitOder(this.state.trump));
        const orderedSameSuitCards = this.sortCards(sameSuitCards, this.getSuitOder(playingCard && playingCard.suit));

        return orderedTrumpCards[0] || orderedSameSuitCards[0];
    }

    checkTrickWinner (cards: Card[], apply: boolean = true) {
        const winner = this.getWinnerCard(cards).player;

        // flag trick winner on each card
        if (apply) {
            cards.forEach(card => {
                card.played = undefined;
                card.winner = winner;
            });
        }

        return winner as number;
    }

    actAI () {
        const cards = this.hands[this.state.currentPlayer].cards;
        const gotoNextTurn = (changeCurrentPlayer: boolean = true) => {
            setTimeout(() => this.nextTurn(changeCurrentPlayer), NEXT_ACTION_TIMEOUT);
        }

        if (this.state.drawingCard) {
            const drawingCard = this.state.drawingCard as Card;
            const cardsOfSuit = cards.filter(card => (
                card.suit === drawingCard.suit || isLeftBower(card, drawingCard.suit)
            ));

            if (
                (
                    (drawingCard.number as number) >= 11 &&
                    cardsOfSuit.length >= 2 &&
                    (
                        this.state.dealer === this.state.currentPlayer ||
                        this.state.dealer === (this.state.currentPlayer + 2) % 4
                    )
                ) ||
                cardsOfSuit.length >= 3
            ) {
                this.onOrderCard();

                if (this.state.dealer !== 0) {
                    gotoNextTurn(false);
                }

            } else {
                this.onPass();
                gotoNextTurn();
            }

        } else if (!this.state.drawingCard && this.state.chooseTrump) {
            let trumpScores: any[] = [];

            for (let i = 0; i < this.state.chooseTrump.length; i++) {
                const trump = this.state.chooseTrump[i];
                const score = cards.reduce((acc, card) => {
                    if (card.suit === trump || isLeftBower(card, trump)) {
                        acc += ((card.number === 11) ? 15 : card.number as number)
                    }
                    return acc;
                }, 0);
                trumpScores.push(score);
            }

            let highestScore = trumpScores.sort().reverse()[0];

            if (highestScore > 32) {
                this.onChooseTrump(this.state.chooseTrump[ trumpScores.indexOf(highestScore) ]);

            } else {
                this.onPass();
                gotoNextTurn();
            }

        } else {
            const suitOrder = this.getSuitOder(this.state.trump);
            const availableCards = this.sortCards(
                cards.filter((card) => isCardAvailable(card, cards, this.state.trump, this.state.playingCard)),
                suitOrder
            );

            const playedCards = this.state.cards.filter(card => card.played !== undefined);
            const winnerCard = this.getWinnerCard(playedCards);

            let playCard: Card;

            const playRandom = () => availableCards[Math.floor(Math.random() * availableCards.length)];
            const playLowestCard = () => availableCards[availableCards.length - 1]

            // select a card that's higher than the target
            const playHighestCard = (card: Card) => {
                let playCard: Card | undefined = undefined;

                let i = availableCards.length;
                while (i--)  {
                    const candidate = availableCards[i];
                    const sorted = this.sortCards([card, candidate], suitOrder);

                    if (sorted[0] !== card) {
                        playCard = candidate;
                        break;
                    }
                }

                // if there's no way to win this trick, play the lowest card
                return (playCard) ? playCard : playLowestCard();
            }

            if (this.state.playedCards >= 2) {
                playCard = ((winnerCard.player as number) - 1 === (this.state.currentPlayer + 2) % 4)
                    ? playLowestCard()
                    : playHighestCard(winnerCard);

            } else if (this.state.playedCards >= 1) {
                playCard = playHighestCard(winnerCard);

            } else {
                playCard = playRandom();
            }

            // Play a random available card
            this.onPlayCard(cards.indexOf(playCard));
        }
    }

    onOrderTrump (orderingPlayer: number, discardIndex?: number) {
        const hand = this.state.hands[orderingPlayer];

        const drawingCard = this.state.drawingCard as Card;
        drawingCard.player = orderingPlayer + 1;

        //
        // AI: auto-select card to discard from hand.
        // AI will select a lower card from a suit other than trump suit
        //
        if (discardIndex === undefined) {
            const orderedCards = sortCards(hand.cards, {
                asc: false,
                trump: drawingCard.suit,
                highestNumber: 11 // J is highest
            });

            discardIndex = hand.cards.indexOf(orderedCards[orderedCards.length - 1]);
        }

        hand.discard(discardIndex);
        hand.draw(drawingCard);
    }

    getPreviousPlayer () {
        return (4 + (this.state.currentPlayer - 1)) % 4;
    }

    onOrderCard = (isPlayerAction: boolean = false) => {
        const drawingCard = this.state.drawingCard as Card;
        const players = this.state.players;

        players[this.state.currentPlayer].balloon = true;
        players[this.state.currentPlayer].balloonText = "I will order it!";

        let callback = undefined;

        const nextState: any = {};

        if (isPlayerAction) {
            nextState.showPlayingAloneDialog = true;
        }

        if (this.state.dealer === 0) {
            nextState.discardNeeded = true;

            // remove balloon after a timeout.
            callback = () => {
                setTimeout(() => {
                    const playersWithoutBalloon = players;
                    if (playersWithoutBalloon[0].balloon) {
                        playersWithoutBalloon[0].balloon = false;
                        this.setState({ players: playersWithoutBalloon });
                    }
                }, NEXT_ACTION_TIMEOUT * 2);
            }

        } else {
            this.onOrderTrump(this.state.dealer);
            this.sortCurrentPlayerHand(drawingCard.suit);

            nextState.hands = this.state.hands;
            nextState.players = players;
            nextState.cards = this.deck.cards;
            nextState.trump = drawingCard.suit;
            nextState.drawingCard = undefined;

            if (!isPlayerAction) {
                this.onRoundStart(nextState);
            }

            callback = (isPlayerAction)
                ? () => this.nextTurn(false)
                : undefined;
        }

        this.setState(nextState, callback);
    }

    onPass = (isPlayerAction: boolean = false) => {
        const players = this.state.players;
        players[this.state.currentPlayer].balloon = true;
        players[this.state.currentPlayer].balloonText = "I will pass!";

        this.setState({ players });

        if (isPlayerAction) {
            this.nextTurn();
        }
    }

    onChooseTrump = (trump: Suit, isPlayerAction: boolean = false) => {
        this.deck.discardRemaining();

        const players = this.players;
        players[this.state.currentPlayer].balloon = true;
        players[this.state.currentPlayer].balloonText = <span>I'll make it <img src={ SUIT_IMAGES[trump] } /></span>;

        const showPlayingAloneDialog = (this.state.currentPlayer === 0);

        const nextState: any = {
            trump,
            players,
            chooseTrump: undefined,
            drawingCard: undefined,
            cards: this.deck.cards,
            showPlayingAloneDialog
        };

        if (!showPlayingAloneDialog) {
            this.onRoundStart(nextState);
            setTimeout(() => this.nextTurn(), NEXT_ACTION_TIMEOUT);

            nextState.currentPlayer = this.state.dealer;
        }

        this.setState(nextState);
    }

    onPlayAlone = () => {
        this.maxPlayedCards = 3;

        const players = this.players;
        players[(this.state.currentPlayer + 2) % 4].team = NOT_IN_GAME_TEAM;

        let currentPlayer = (this.state.currentPlayer + 1) % 4;

        // if next player is not in game, increment again
        if (players[currentPlayer].team === NOT_IN_GAME_TEAM) {
          currentPlayer = (currentPlayer + 1) % 4;
        }

        const callback = (currentPlayer !== 0)
          ? () => this.nextTurn(false)
          : undefined;

        this.setState(this.onRoundStart({
          players,
          showPlayingAloneDialog: false,
          currentPlayer
        }), callback);
    }

    onPlayDuo = () => {
        this.maxPlayedCards = 4;

        const currentPlayer = (this.state.dealer + 1) % 4;
        const callback = (currentPlayer !== 0)
          ? () => this.nextTurn(false)
          : undefined;

        this.setState(this.onRoundStart({
            players: this.players,
            showPlayingAloneDialog: false,
            currentPlayer
        }), callback);
    }

    sortCurrentPlayerHand (trumpSuit?: Suit) {
        this.state.hands[0].cards = sortCards(this.state.hands[0].cards, {
            asc: true,
            suits: this.getSuitOder(trumpSuit),
            highestNumber: 11 // J is highest
        });
    }

    sortCards (cards: Card[], suitOrder?: Suit[]) {
        return sortCards(cards, {
            asc: false,
            trump: this.state.trump,
            suits: suitOrder,
            leftBower: getLeftBowerSuit(this.state.trump),
            highestNumber: 11
        });

    }


    getSuitOder (trumpSuit?: Suit) {
        let suits = getAllSuits();

        if (trumpSuit) {
            suits = suits.filter(suit => suit !== trumpSuit);
            suits.unshift(trumpSuit);
        }

        return suits;
    }

    public render () {
        const isDialogActive = (this.state.drawingCard && (this.state.currentPlayer === 0 || this.state.discardNeeded) && !this.state.trump);

        // const partnerName = this.players[2] && this.players[2].name;
        // const opponents = getTeamPlayers(this.players, 1);
        const hasOverlay = (isDialogActive || this.state.showPlayingAloneDialog);

        return <section className={`board ${(this.state.trump || this.state.chooseTrump) ? "has-trump": ""}`}>
            <ScoreboardComponent players={this.state.players} rounds={this.state.rounds} />
            <div className="confetti">
                <Confetti active={(this.state.endOfRound && this.state.rounds[this.state.currentRound].winner === 0)} config={ confettiConfig }/>
            </div>

            {(this.state.endOfRound)
                ? <EndRoundComponent
                    onPlayAnotherHand={this.onPlayAnotherHand}
                    onResetScores={this.onResetScores}
                    currentRound={this.state.currentRound}
                    rounds={this.state.rounds} />
                : null}

            {this.state.players.map((opponent, i) => (
                <div
                    key={`player-${i}`}
                    className={`
                        player
                        i-${i+1}
                        t-${opponent.team}
                        ${(this.state.dealer === i) ? 'dealer' : ''}
                        ${(this.state.currentPlayer === i) ? 'current' : ''}
                    `}>
                    { (opponent.image)
                        ? <img src={opponent.image} />
                        : null }
                    <span className={`balloon ${opponent.balloon ? "active" : ""}`}>{opponent.balloonText}</span>

                    <span className="name">
                        {opponent.name}<br className="xs-only" />
                        <span className={`team ${opponent.team}`}>{opponent.team}</span>
                        <span className="dealer">D</span>
                    </span>
                    <span className="tricks">{ opponent.tricks } tricks</span>
                </div>
            ))}

            {/* playing alone dialog */}
            {(this.state.showPlayingAloneDialog && !this.state.discardNeeded)
              ?
                <div className={`dialog-overlay`}>
                    <div className="dialog play-alone">
                        <h2>Do you want to "go it alone"?</h2>
                        <p>
                            You can win a max of 4 points alone...<br />
                            or a max of 2 points with your partner.<br />
                            What do you want to do?
                        </p>
                        <div className="actions">
                            <button onClick={this.onPlayAlone.bind(undefined, true)}>Go it alone</button>
                            <button onClick={this.onPlayDuo.bind(undefined, true)}>Play with my partner</button>
                        </div>
                    </div>
                </div>
              : null }

            {/* drawing card */}
            <div className={`dialog-overlay  ${isDialogActive ? "" : "inactive"} ${(this.state.discardNeeded) ? "discard-needed" : ""}`}>
              <div className={`dialog`}>
                  <p>The candidate suit is {this.state.drawingCard && getSuitName(this.state.drawingCard.suit)}</p>
                  {(this.state.discardNeeded)
                      ?
                          <p>Discard one card for replacement.</p>
                      :
                          <div className="actions">
                              <button className="order" onClick={this.onOrderCard.bind(undefined, true)}>Order it up! 👍</button>
                              <button className="pass" onClick={this.onPass.bind(undefined, true)}>Pass 👈</button>
                          </div>
                      }
              </div>
            </div>

            {/* trump */}
            {(this.state.chooseTrump)
                ?
                    <div className="dialog">
                        <p>{(this.state.currentPlayer === 0) ? "Choose a trump suit:" : "Choosing trump suit."}</p>

                        {(this.state.currentPlayer === 0)
                            ?
                                <div className="choose-suit">
                                    {this.state.chooseTrump.map((suit) => (
                                        <button key={suit} className="trump-option" onClick={this.onChooseTrump.bind(undefined, suit, true)}>
                                            <img src={SUIT_IMAGES[suit]} alt=""/>
                                        </button>
                                    ))}
                                </div>
                            : null }

                        {(this.state.currentPlayer === 0)
                            ? <button className="pass" onClick={this.onPass.bind(undefined, true)}>Pass 👈</button>
                            : null}
                    </div>
                : null }

            {/* trump */}
            {(this.state.trump)
                ?
                    <div className="trump">
                        <span>Trump is:</span>
                        <img src={SUIT_IMAGES[this.state.trump]} />
                    </div>
                : null }

            {/* All cards */}
            {this.state.cards.map((card, i) => {
                const playerNum = card.winner || card.player as number;
                const player = playerNum !== undefined ? this.players[playerNum - 1] : undefined;
                const faceUp = card.winner !== undefined || (playerNum === 1) || (card === this.state.drawingCard);
                const onClick = (playerNum === 1) ? this.onPlayCard : undefined;
                const enabled = (
                    player !== undefined &&
                    !player.played && (
                        this.state.discardNeeded || (
                            this.state.currentPlayer === 0 && (
                                this.state.trump !== undefined && (
                                    this.state.playingCard == undefined ||
                                    isCardAvailable(card, this.state.hands[playerNum - 1].cards, this.state.trump, this.state.playingCard)
                                )
                            )
                        )
                    )
                );

                let className = (playerNum && `player-${playerNum}`) || 'board';
                className += (this.state.discardNeeded)
                  ? " discard-needed"
                  : (hasOverlay)
                    ? " has-overlay"
                    : "";

                // player's team is present only after trump is ordered
                if (
                    (this.state.trump || this.state.endOfRound) &&
                    !this.state.showPlayingAloneDialog &&
                    player
                ) {
                    className += " t-" + player.team;
                }

                if (card.winner) {
                    // winner animation goes to the center of player's hand.
                    i = 2;

                } else if (playerNum) {
                    // position the card from 0 to 4
                    i = this.state.hands[playerNum - 1].cards.indexOf(card);
                }

                return <CardComponent
                    player={playerNum}
                    key={`card-${card.suit}${card.number}`}
                    className={className}
                    faceUp={faceUp}
                    onClick={onClick}
                    enabled={enabled}
                    i={i}
                    {...card} />
            })}

        </section>;
    }

}



// WEBPACK FOOTER //
// ./src/components/BoardComponent.tsx